import {
	checkNearestValidAmount,
	type CURRENCYCODETYPE,
	type CURRENCYSYMBOLTYPE,
	formatNumberWithCommas,
	getCleanedValue,
	handleDecimalPoints,
	type IWalletProps,
} from 'BreetHelpers';
import { type ChangeEvent, useCallback } from 'react';

type updateValueType = (value: string, validateAmount: validateAmountType) => void;
type validateAmountType = (cleanedValue: string, setErrorFn: (message: string) => void, clearErrorsFn: () => void) => void;

export const useCurrencyFormAmountApi = ({
	withdrawalMaxAmount,
	withdrawalMinAmount,
	userWalletProps,
	currencySymbol,
	currencyCode,
}: {
	withdrawalMaxAmount: number;
	withdrawalMinAmount: number;
	userWalletProps: IWalletProps;
	currencySymbol: CURRENCYSYMBOLTYPE;
	currencyCode: CURRENCYCODETYPE;
}) => {
	const validateAmount = useCallback(
		(cleanedValue: string, setErrorFn: (message: string) => void, clearErrorsFn: () => void) => {
			const inputAmount = Number(cleanedValue.replace(/,/g, ''));

			if (inputAmount > userWalletProps.walletBalance) {
				setErrorFn('Insufficient balance');
				return;
			}

			if (inputAmount < withdrawalMinAmount) {
				setErrorFn(`The minimum withdrawal amount is ${currencySymbol} ${formatNumberWithCommas(withdrawalMinAmount)}`);
				return;
			}

			if (!!withdrawalMaxAmount && inputAmount > withdrawalMaxAmount) {
				setErrorFn(`The maximum withdrawal amount is ${currencySymbol} ${formatNumberWithCommas(withdrawalMaxAmount)}`);
				return;
			}

			if (currencyCode === 'NGN') {
				const nearestValidAmountError = checkNearestValidAmount(inputAmount, currencyCode);

				if (nearestValidAmountError) {
					clearErrorsFn();
					setErrorFn(nearestValidAmountError);
					return;
				}
			}

			clearErrorsFn();
		},
		[userWalletProps.walletBalance, withdrawalMinAmount, withdrawalMaxAmount, currencyCode, currencySymbol]
	);

	const commitAmount = useCallback(
		(value: string, updateValue: updateValueType) => {
			let cleanedValue = getCleanedValue(value);

			cleanedValue = handleDecimalPoints(cleanedValue);

			updateValue(cleanedValue, validateAmount);
		},
		[validateAmount]
	);

	const handleAmountChange = useCallback(
		(errorMsg: string, updateValueFn: updateValueType, clearErrorFn: () => void) => (event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			if (!!errorMsg || !value) clearErrorFn();

			commitAmount(value, updateValueFn);
		},
		[commitAmount]
	);

	return {
		handleAmountChange,
		commitAmount,
		validateAmount,
	};
};
